import { useSearchParams } from 'react-router'

import { EMPTY_OBJECT } from '../../../../../constants/common'
import { FILTER_NAME_SUB_MENU } from '../../../../../constants/filter'
import { PAGE_SIZE_PARAM_KEY } from '../../../../../constants/route'
import { useInfiniteItemsLoader } from '../../../../../hooks'
import { getItemsFromIdwellApiResponse } from '../../../../../utils/api'
import { getSubSearchParam } from '../../../../../utils/route'
import { useAsyncEntity } from '../../../../Entity'
import {
  getEmailMessages,
  getEmailProfileWidgetsInfo,
  getEmailThreadProfile,
  updateEmail,
} from '../../../Email.api'
import { EMAIL_PROFILE_WIDGET_NAME_AI_SUMMARY } from '../../../Email.constants'
import {
  useEmailMessageCompletedRealtime,
  useEmailProfileAISummaryData,
  useEmailProfileRead,
  useEmailProfileWidgetsData,
} from '../../../Email.hooks'
import {
  getEmailMessagesWithMessageCompleted,
  isEmailMessagePending,
} from '../../../Email.utils'

import {
  MESSAGE_THRESHOLD_END_INDEX,
  MESSAGES_API_PARAM_PAGE_SIZE,
} from './useEmailThreadProfileLoaders.constants'
import { getEmailThreadProfileNextUpdatedData } from './useEmailThreadProfileLoaders.utils'

export function useEmailThreadProfileLoaders({
  getMessages: getMessagesApi = getEmailMessages,
  getProfileData: getProfileDataApi = getEmailThreadProfile,
  getSummaryData: getSummaryDataApi = getEmailProfileWidgetsInfo,
  isReadEnabled = true,
  mailUuid,
  readApi,
  withWidgets = true,
  onSuccessRead,
}) {
  const [searchParams] = useSearchParams()
  const sub = getSubSearchParam(searchParams)

  useEmailProfileRead({
    isEnabled: isReadEnabled,
    mailUuid,
    readApi,
    single: false,
    onSuccessRead,
  })

  async function loadAISummaryWidgetData(apiConfig) {
    return getSummaryDataApi(
      {
        [FILTER_NAME_SUB_MENU]: sub,
        email_thread_uuid: mailUuid,
        widget_name: EMAIL_PROFILE_WIDGET_NAME_AI_SUMMARY,
      },
      apiConfig
    )
  }

  async function getWidgetsData() {
    return getEmailProfileWidgetsInfo({
      [FILTER_NAME_SUB_MENU]: sub,
      email_thread_uuid: mailUuid,
    })
  }

  async function getProfileData(apiConfig = EMPTY_OBJECT) {
    return getProfileDataApi({
      ...apiConfig,
      params: {
        [FILTER_NAME_SUB_MENU]: sub,
      },
      uuid: mailUuid,
    })
  }

  async function loadMessages(params) {
    return getMessagesApi({ params })
  }

  async function updateProfileData(data) {
    return updateEmail(mailUuid, data)
  }

  const {
    entityData,
    handleEntityInit,
    handleEntityReload,
    handleUpdateEntity,
    isInitiated,
    isLoading,
    serverError,
  } = useAsyncEntity({
    getNextUpdatedEntityData: getEmailThreadProfileNextUpdatedData,
    getProfileData,
    updateProfileData,
  })

  const { loadWidgetsData, widgetsData } = useEmailProfileWidgetsData({
    enabled: withWidgets,
    getWidgetsData,
    isInitiated,
  })

  const { loadSummaryData, setSummaryWidgetData, summaryData } =
    useEmailProfileAISummaryData({
      getSummaryData: getSummaryDataApi ? loadAISummaryWidgetData : null,
      isInitiated,
    })

  const {
    getThresholdItemProps: getMessageThresholdItemsProps,
    items: messages,
    loadingMore: loadingMoreMessage,
    loadInitial: loadEmailMessages,
    setItems: setMessages,
    totalCount: totalCountMessages,
  } = useInfiniteItemsLoader({
    apiParams: {
      [FILTER_NAME_SUB_MENU]: sub,
      [PAGE_SIZE_PARAM_KEY]: MESSAGES_API_PARAM_PAGE_SIZE,
      email_thread_uuid: mailUuid,
    },
    getData: loadMessages,
    getDataItems: getItemsFromIdwellApiResponse,
    preloading: !isInitiated,
    thresholdEndIndex: MESSAGE_THRESHOLD_END_INDEX,
    withPagination: true,
  })

  async function handleReloadProfile() {
    return handleEntityReload().then(() =>
      Promise.allSettled([
        loadEmailMessages(),
        withWidgets ? loadWidgetsData() : null,
        loadSummaryData(),
      ])
    )
  }

  function handleMessageCompleted(payload) {
    setMessages(prevMessages =>
      getEmailMessagesWithMessageCompleted(prevMessages, payload)
    )
  }

  function handleInitialSubscribe() {
    if (messages?.some(isEmailMessagePending)) {
      loadEmailMessages()
    }
  }

  useEmailMessageCompletedRealtime({
    channel: entityData?.realtime_channel,
    enabled: isInitiated,
    onInitialSubscribe: handleInitialSubscribe,
    onMessageCompleted: handleMessageCompleted,
    onResync: handleReloadProfile,
  })

  return {
    entityData,
    getMessageThresholdItemsProps,
    getWidgetsData,
    handleEntityInit,
    handleReloadProfile,
    handleUpdateEntity,
    isInitiated,
    isLoading,
    loadAISummaryWidgetData,
    loadEmailMessages,
    loadingMoreMessage,
    loadSummaryData,
    loadWidgetsData,
    messages,
    serverError,
    setMessages,
    setSummaryWidgetData,
    summaryData,
    totalCountMessages,
    widgetsData,
  }
}
